import React, { useId } from 'react'
import { FaChevronDown } from 'react-icons/fa'

/**
 * A collapsed tier inside a settings section.
 *
 * The settings pane is one scrolling page, so every section has to stay a
 * bounded height. The two device editors are not: the printer one carries
 * three printer slots plus margins, DPI, scale and position; the camera one
 * carries quality, rotation, presets and the raw camera config tree. Most
 * booths touch none of that — under a quarter run a second printer at all —
 * so those tiers fold up behind a named row and the common case stays short.
 *
 * `enabled={false}` renders the children bare. The editors use that to keep
 * their standalone modal presentation exactly as it was.
 *
 * Styled flat, like every row in the pane: no card, a hairline above.
 */
interface SettingsDisclosureProps {
  title: string
  /** One line under the title saying what is inside. */
  hint?: string
  /** Right-aligned while collapsed — e.g. "2 configured". */
  summary?: string
  open: boolean
  onToggle: () => void
  enabled?: boolean
  children: React.ReactNode
}

export const SettingsDisclosure: React.FC<SettingsDisclosureProps> = ({
  title,
  hint,
  summary,
  open,
  onToggle,
  enabled = true,
  children
}) => {
  const bodyId = useId()
  if (!enabled) return <>{children}</>

  // Boxless like the rows around it: the parent's hairlines separate it, and when
  // open the body hangs off a light rule with the same hairlines between fields.
  return (
    <div className="py-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full items-center justify-between gap-3 py-1 text-left"
      >
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-700">{title}</span>
          {hint && <span className="block text-xs text-gray-500">{hint}</span>}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {!open && summary && <span className="text-xs text-gray-500">{summary}</span>}
          <FaChevronDown
            className={`h-3 w-3 text-gray-400 transition-transform motion-reduce:transition-none ${
              open ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </span>
      </button>
      {open && (
        <div id={bodyId} className="mt-2 divide-y divide-gray-100 border-l-2 border-gray-100 pl-4">
          {children}
        </div>
      )}
    </div>
  )
}

export default SettingsDisclosure
